import { useCallback } from 'react';
import {
  type FieldValues,
  type Path,
  // biome-ignore lint/style/noRestrictedImports: wrapper interno
  useController as useRHFController,
} from 'react-hook-form';
import { useTriggerComputedSync } from './useTriggerComputedSync.ts';

/**
 * RHF accetta in `field.onChange` sia eventi DOM sia valori raw.
 * Per `syncComputedValues` serve il valore effettivo da passare
 * nell'evento `{ path, value }`.
 */
export function extractValueFromChangeArg(arg: any): unknown {
  const target = arg?.target;
  if (!target) return arg;
  if (target.type === 'checkbox') return target.checked;
  return target.value;
}

/**
 * Wrapper di `useController` (react-hook-form) con onChange arricchito
 * che schedula automaticamente `syncComputedValues`.
 *
 * Utilizzare al posto di `useController` da `react-hook-form` in tutti
 * i componenti. Per input con `FormItemContext` e schema Yup, preferire
 * `useInputController`.
 */
export function useController<
  T = any,
  TFieldValues extends FieldValues = Record<string, T>,
>(name: Path<TFieldValues>, options?: { defaultValue?: any }) {
  const controller = useRHFController<TFieldValues>({
    name,
    defaultValue: options?.defaultValue,
  });
  const triggerSync = useTriggerComputedSync();

  const enhancedOnChange: typeof controller.field.onChange = useCallback(
    (...args) => {
      const value = extractValueFromChangeArg(args[0]);
      controller.field.onChange(...args);
      triggerSync({ path: name, value });
    },
    [controller.field.onChange, name, triggerSync],
  );

  return {
    ...controller,
    field: {
      ...controller.field,
      onChange: enhancedOnChange,
    },
  };
}
